import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Gift, Star, Check, Sparkles, PartyPopper, Rocket } from "lucide-react";
import { api } from "../lib/api";
import Confetti from "./Confetti";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";

const BUSINESS_TYPES = [
  { v: "libero_professionista", l: "Libero Professionista o Freelance" },
  { v: "ditta_individuale", l: "Ditta Individuale Artigiano o Commerciante" },
  { v: "societa", l: "Società di Capitali o Persone" },
  { v: "senza_piva", l: "Soggetto senza Partita IVA" },
];
const TAX_REGIMES = [
  { v: "forfettario", l: "Regime Forfettario" },
  { v: "semplificato", l: "Regime Semplificato" },
  { v: "ordinario", l: "Regime Ordinario" },
  { v: "occasionale", l: "Prestazione Occasionale" },
];
const FEATURES = [
  "Pacchetti Tier & Add-on interattivi",
  "Motore Fiscale Italiano automatico",
  "Firma su schermo & PDF A4",
  "Riepilogo Acconto e Saldo",
  "Tracciamento Aperture & Solleciti WhatsApp",
  "Codici Promo & Sconti",
  "Avanzamento Lavori in tempo reale",
];

const emptyForm = () => ({
  profession: "", business_type: "libero_professionista", tax_regime_label: "forfettario",
  rating: 0, favorite_features: [], improvements: "", additions: "",
  testimonial_text: "", consent_public: false,
});

export default function BetaProgram() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState("form"); // form | reward | done
  const [showConfetti, setShowConfetti] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [hoverStar, setHoverStar] = useState(0);
  const [claiming, setClaiming] = useState(false);
  const [rewardCode, setRewardCode] = useState("");
  const [rewardChoice, setRewardChoice] = useState("");

  const load = async () => {
    try { const { data } = await api.get("/beta/status"); setStatus(data); } catch (e) { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const toggleFeature = (f) => setForm((s) => ({
    ...s, favorite_features: s.favorite_features.includes(f)
      ? s.favorite_features.filter((x) => x !== f) : [...s.favorite_features, f],
  }));

  const submit = async () => {
    if (!form.rating) return toast.error("Assegna una valutazione da 1 a 5 stelle");
    if (!form.improvements.trim()) return toast.error("Indica cosa miglioreresti");
    setSaving(true);
    try {
      await api.post("/beta/feedback", {
        profession: form.profession, business_type: form.business_type,
        tax_regime_label: form.tax_regime_label, rating: Number(form.rating),
        favorite_features: form.favorite_features,
        favorite_feature: form.favorite_features[0] || "",
        improvements: form.improvements, additions: form.additions,
        suggestions: form.improvements,
        testimonial_text: form.testimonial_text, consent_public: form.consent_public,
      });
      setPhase("reward");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nell'invio del feedback");
    }
    setSaving(false);
  };

  const claimReward = async (choice) => {
    setClaiming(true);
    try {
      const { data } = await api.post("/beta/reward", { choice });
      setRewardChoice(choice);
      if (choice === "code") setRewardCode(data.code);
      setPhase("done");
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 4200);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel riscatto");
    }
    setClaiming(false);
  };

  const closeAll = async () => {
    setOpen(false); setPhase("form"); setForm(emptyForm()); setRewardCode(""); setRewardChoice("");
    await load();
  };

  if (!status) return null;

  if (status.submitted || status.piano_abbonamento !== "free") return null;

  return (
    <>
      <div data-testid="beta-banner" className="mb-6 rounded-xl border border-indigo-200 bg-indigo-600 text-white px-5 py-4 flex items-center gap-4 flex-wrap shadow-sm">
        <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center shrink-0"><Gift style={{ width: 20, height: 20 }} /></div>
        <div className="flex-1 min-w-[220px]">
          <p className="font-bold leading-snug">🎁 Programma Beta Tester: scegli il tuo premio</p>
          <p className="text-sm text-indigo-100">Compila il questionario di 60 secondi e ricevi 30 giorni di Pro.</p>
        </div>
        <Button data-testid="beta-banner-cta" onClick={() => setOpen(true)} className="bg-card text-indigo-700 hover:bg-indigo-50 font-semibold shrink-0">
          <Sparkles style={{ width: 16, height: 16 }} className="mr-1.5" /> Compila Ora
        </Button>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) closeAll(); else setOpen(true); }}>
        <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto" data-testid="beta-questionnaire-dialog">
          {phase === "form" && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2"><Sparkles style={{ width: 18, height: 18 }} className="text-primary" /> Questionario Beta — 60 secondi</DialogTitle>
                <DialogDescription>Rispondi e scegli subito il tuo premio.</DialogDescription>
              </DialogHeader>
              <div className="space-y-5">
                <div>
                  <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Professione / Settore</Label>
                  <Input data-testid="beta-profession" value={form.profession} onChange={(e) => set("profession", e.target.value)} placeholder="Es. Web Designer, Fotografo…" className="mt-1.5" />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Tipo di Attività</Label>
                    <Select value={form.business_type} onValueChange={(v) => set("business_type", v)}>
                      <SelectTrigger data-testid="beta-business-type" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{BUSINESS_TYPES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Regime Fiscale</Label>
                    <Select value={form.tax_regime_label} onValueChange={(v) => set("tax_regime_label", v)}>
                      <SelectTrigger data-testid="beta-regime" className="mt-1.5"><SelectValue /></SelectTrigger>
                      <SelectContent>{TAX_REGIMES.map((r) => <SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-800">1. Quanto è stato semplice creare il preventivo e preciso il calcolo fiscale?</Label>
                  <div className="flex items-center gap-1.5 mt-2" data-testid="beta-rating">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <button key={n} type="button" data-testid={`beta-star-${n}`}
                        onMouseEnter={() => setHoverStar(n)} onMouseLeave={() => setHoverStar(0)}
                        onClick={() => set("rating", n)} className="transition-transform hover:scale-110">
                        <Star style={{ width: 30, height: 30 }}
                          className={(hoverStar || form.rating) >= n ? "text-amber-400" : "text-slate-200"}
                          fill={(hoverStar || form.rating) >= n ? "currentColor" : "none"} />
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-800">2. Quali funzionalità ti farebbero risparmiare più tempo? (puoi selezionarne più di una)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {FEATURES.map((f, i) => (
                      <button key={f} type="button" data-testid={`beta-feature-${i}`}
                        onClick={() => toggleFeature(f)}
                        className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${form.favorite_features.includes(f) ? "bg-primary text-white border-primary" : "bg-card text-slate-600 border-slate-200 hover:border-primary/40"}`}>
                        {form.favorite_features.includes(f) && <Check style={{ width: 13, height: 13 }} className="inline mr-1" />}{f}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-800">3. Cosa miglioreresti? *</Label>
                  <Textarea data-testid="beta-improvements" rows={3} value={form.improvements} onChange={(e) => set("improvements", e.target.value)} placeholder="Scrivi liberamente…" className="mt-2" />
                </div>

                <div>
                  <Label className="text-sm font-semibold text-slate-800">4. Cosa aggiungeresti per il tuo lavoro?</Label>
                  <Textarea data-testid="beta-additions" rows={3} value={form.additions} onChange={(e) => set("additions", e.target.value)} placeholder="Idee, integrazioni, funzioni che ti servirebbero…" className="mt-2" />
                  <Input data-testid="beta-testimonial" value={form.testimonial_text} onChange={(e) => set("testimonial_text", e.target.value)}
                    placeholder="(Opzionale) Una frase per comparire tra i nostri testimonial" className="mt-3" />
                  <label className="flex items-start gap-2 text-sm text-slate-600 mt-3">
                    <input type="checkbox" data-testid="beta-consent" checked={form.consent_public} onChange={(e) => set("consent_public", e.target.checked)} className="rounded border-slate-300 mt-0.5" />
                    Autorizzo l'utilizzo del mio feedback e nome/professione come recensione pubblica
                  </label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={closeAll}>Annulla</Button>
                <Button data-testid="beta-submit" onClick={submit} disabled={saving} className="font-semibold">
                  {saving ? "Invio…" : "Invia e Scegli Premio"}
                </Button>
              </DialogFooter>
            </>
          )}

          {phase === "reward" && (
            <div className="py-4" data-testid="beta-reward-chooser">
              <div className="text-center mb-5">
                <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-3"><PartyPopper style={{ width: 28, height: 28 }} className="text-emerald-600" /></div>
                <h2 className="text-xl font-extrabold text-slate-900">Grazie di cuore! 🎉</h2>
                <p className="text-slate-600 mt-1 text-sm">Ecco il tuo premio:</p>
              </div>
              <div className="grid gap-4 max-w-sm mx-auto">
                <button data-testid="beta-reward-trial" onClick={() => claimReward("trial")} disabled={claiming}
                  className="text-left rounded-xl border-2 border-emerald-200 hover:border-emerald-500 p-5 transition-colors disabled:opacity-50">
                  <Rocket style={{ width: 24, height: 24 }} className="text-emerald-600 mb-2" />
                  <p className="font-bold text-slate-900">30 Giorni di Pro</p>
                  <p className="text-sm text-slate-500 mt-1">Prova gratuita immediata del Piano Pro (ideale per il mensile).</p>
                </button>
              </div>
            </div>
          )}

          {phase === "done" && (
            <div className="text-center py-6" data-testid="beta-congrats">
              <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4"><Check style={{ width: 32, height: 32 }} className="text-emerald-600" /></div>
              <h2 className="text-2xl font-extrabold text-slate-900">Premio attivato! 🎉</h2>
              {rewardChoice === "code" ? (
                <>
                  <p className="text-slate-600 mt-2 max-w-sm mx-auto">Usa questo codice al checkout per avere il <strong>-40% sul piano Annuale</strong>:</p>
                  <div data-testid="beta-reward-code-value" className="inline-block mt-4 px-5 py-3 rounded-lg bg-indigo-50 text-indigo-700 font-mono text-lg font-extrabold tracking-wider">{rewardCode}</div>
                </>
              ) : (
                <p className="text-slate-600 mt-2 max-w-sm mx-auto">Il tuo <strong>Piano Pro</strong> è attivo gratuitamente per 30 giorni. Buon lavoro!</p>
              )}
              <div className="mt-6"><Button data-testid="beta-congrats-close" onClick={closeAll} className="font-semibold">Continua</Button></div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {showConfetti && <Confetti />}
    </>
  );
}
